// AIBILL1 (DEC-596; docs/build-briefs/AIBILL1-ai-help-under-billing.md): the one "AI help" section on Settings, Billing.
// The switch, how the location is billed, this month's charge, the monthly limit and the last few charges, in one place.
// Every figure is read from the server; this file computes nothing about money except rounding the stored charge for display.
import { aiDollarInput, aiLimitWords, AI_LIMITS_LINE, type ReportAiBillingView } from "./reportAiBilling.ts";
import { aiBilledWords } from "./reportAiHelpModel.ts";
import { exactDollars } from "./franchisorListModel.ts";
import { hoverHelp } from "./hoverHelp.ts";

export interface ReportAiHelpView {
  enabled: boolean;
  rateSet: boolean;
  billedOnAccount: string | null;
  recent: Array<{
    at: string;
    who: string;
    feature: string;
    amountCents: number;
    lessThanOneCent: boolean;
  }>;
}
interface Reply<T> {
  status: number;
  body: T | null;
}
export interface ReportAiHelpHost {
  readHelp(): Promise<Reply<ReportAiHelpView & { message?: string }>>;
  readBilling(): Promise<Reply<ReportAiBillingView & { message?: string }>>;
  /** Whether the signed-in person may change the switch (admin.location_settings.manage). The server checks it again. */
  canSwitch(): boolean;
  setEnabled(enabled: boolean): Promise<Reply<{ enabled?: boolean; message?: string }>>;
  saveCap(input: {
    scope: "account" | "member" | "location";
    scopeId: string;
    capDollars: string | null;
  }): Promise<Reply<{ message?: string }>>;
  /** A limit was saved: the AI usage card below reloads. */
  changed(): void;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
  testId?: string,
): HTMLElementTagNameMap[K] {
  const made = document.createElement(tag);
  if (className !== undefined) made.className = className;
  if (text !== undefined) made.textContent = text;
  if (testId !== undefined) made.dataset["testid"] = testId;
  return made;
}

function monthName(month: string): string {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return "";
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

function dayText(iso: string): string {
  const parsed = new Date(iso);
  return Number.isFinite(parsed.getTime())
    ? parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";
}

export function createReportAiHelp(
  hostElement: HTMLElement,
  host: ReportAiHelpHost,
): { load(): Promise<void>; reset(): void } {
  const section = node("section", "cr-ai-settings-card cr-ai-help", undefined, "ai-help-section");
  section.hidden = true;
  hostElement.append(section);
  let generation = 0;

  function reset(): void {
    generation += 1;
    section.replaceChildren();
    section.hidden = true;
  }

  function row(list: HTMLElement, label: string, testId: string, help = ""): HTMLElement {
    const term = node("dt", "cr-ai-help-term", label);
    if (help !== "") hoverHelp(term, help);
    const value = node("dd", "cr-ai-help-value", undefined, testId);
    list.append(term, value);
    return value;
  }

  function drawStatus(value: HTMLElement, help: ReportAiHelpView, expected: number): void {
    const stateText = (on: boolean): string =>
      on ? "On. Your team can draft reports with AI." : "Off. Your team cannot use AI help.";
    const state = node("span", "cr-ai-help-state", stateText(help.enabled), "ai-help-state");
    state.setAttribute("role", "status");
    if (!host.canSwitch()) {
      value.append(state);
      return;
    }
    const input = node("input", "cr-ai-enable-switch");
    input.type = "checkbox";
    input.setAttribute("role", "switch");
    input.setAttribute("aria-label", "Use AI help");
    input.dataset["testid"] = "ai-help-switch";
    input.checked = help.enabled;
    input.onchange = () => {
      const wanted = input.checked;
      input.disabled = true;
      void host
        .setEnabled(wanted)
        .then((saved) => {
          if (expected !== generation) return;
          if (saved.status !== 200) {
            input.checked = !wanted;
            state.textContent = saved.body?.message ?? "Could not save AI settings.";
          } else {
            input.checked = saved.body?.enabled ?? wanted;
            state.textContent = stateText(input.checked);
          }
        })
        .catch(() => {
          if (expected === generation) {
            input.checked = !wanted;
            state.textContent = "Could not save AI settings.";
          }
        })
        .finally(() => {
          if (expected === generation) input.disabled = false;
        });
    };
    value.append(input, state);
  }

  function drawBilled(value: HTMLElement, help: ReportAiHelpView): void {
    value.textContent = aiBilledWords(help.rateSet, help.billedOnAccount);
  }

  function drawMonth(value: HTMLElement, billing: ReportAiBillingView): void {
    const requests = billing.people.reduce((total, person) => total + person.requests, 0);
    if (requests === 0 && billing.totalCents === 0) {
      value.textContent = "No AI requests yet this month.";
      return;
    }
    value.textContent = `${exactDollars(billing.totalCents)} from ${requests} AI ${requests === 1 ? "request" : "requests"}${billing.closed ? "" : " so far"}.`;
  }

  function drawLimits(value: HTMLElement, billing: ReportAiBillingView, expected: number): void {
    const caps = billing.caps.filter((cap) => cap.scope === "location");
    if (caps.length === 0) {
      value.textContent = "No limit";
      return;
    }
    for (const cap of caps) {
      const line = node("div", "cr-ai-help-limit");
      if (caps.length > 1) line.append(node("span", "cr-ai-help-limit-name", cap.name));
      if (!cap.editable) {
        line.append(node("span", undefined, aiLimitWords(cap.capDollars)));
        value.append(line);
        continue;
      }
      const form = node("form", "cr-ai-help-limit-form");
      const { field, input } = aiDollarInput(cap.capDollars, `${cap.name} monthly limit in dollars`);
      input.placeholder = "No limit";
      input.dataset["testid"] = "ai-help-limit-input";
      input.setAttribute("aria-description", "Dollars per calendar month. Blank means no limit.");
      const units = node("span", "cr-ai-help-units", "per month, blank for no limit");
      const save = node("button", "btn-primary", "Save location limit", "ai-help-limit-save");
      save.type = "submit";
      const message = node("span", "cr-ai-cap-status");
      message.setAttribute("role", "status");
      form.append(field, units, save, message);
      form.onsubmit = (event) => {
        event.preventDefault();
        const dollars = input.value.trim();
        if (dollars !== "" && !/^(0|[1-9][0-9]*)$/.test(dollars)) {
          message.textContent = "Enter whole dollars.";
          return;
        }
        input.disabled = true;
        save.disabled = true;
        message.textContent = "";
        void host
          .saveCap({ scope: "location", scopeId: cap.scopeId, capDollars: dollars === "" ? null : dollars })
          .then((saved) => {
            if (expected !== generation) return;
            if (saved.status === 200 || saved.status === 201) {
              host.changed();
              void load();
            } else message.textContent = saved.body?.message ?? "Could not save the limit.";
          })
          .catch(() => {
            if (expected === generation) message.textContent = "Could not save the limit.";
          })
          .finally(() => {
            if (expected === generation) {
              input.disabled = false;
              save.disabled = false;
            }
          });
      };
      line.append(form);
      value.append(line);
    }
    value.append(node("p", "admin-field-help", AI_LIMITS_LINE));
  }

  function drawRecent(help: ReportAiHelpView): HTMLElement {
    const block = node("div", "cr-ai-help-recent");
    block.append(node("h4", undefined, "Recent charges"));
    if (help.recent.length === 0) {
      block.append(node("p", "so-state", "No AI charges yet.", "ai-help-recent-empty"));
      return block;
    }
    const table = node("table", "so-table cr-ai-help-table", undefined, "ai-help-recent");
    const head = node("tr");
    for (const [index, label] of ["Date", "Who", "Feature", "Amount"].entries()) {
      const th = node("th", index === 3 ? "cr-ai-number" : undefined, label);
      th.scope = "col";
      head.append(th);
    }
    const thead = node("thead");
    thead.append(head);
    const body = node("tbody");
    for (const charge of help.recent) {
      const tr = node("tr");
      const amount = node("td", "cr-ai-number", charge.lessThanOneCent ? "Less than $0.01" : exactDollars(charge.amountCents));
      tr.append(node("td", "cr-ai-date", dayText(charge.at)), node("td", undefined, charge.who), node("td", undefined, charge.feature), amount);
      body.append(tr);
    }
    table.append(thead, body);
    const scroll = node("div", "cr-ai-usage-scroll");
    scroll.tabIndex = 0;
    scroll.setAttribute("role", "region");
    scroll.setAttribute("aria-label", "Recent AI charges");
    scroll.append(table);
    block.append(scroll);
    return block;
  }

  // DEC-597: the section's description is printed under its title, not hidden in hover help.
  function head(): HTMLElement[] {
    return [
      node("h3", undefined, "AI help"),
      node(
        "p",
        "admin-field-help",
        "AI help drafts a report when someone describes it in the report builder. Requests stop at the monthly limit until next month.",
        "ai-help-description",
      ),
    ];
  }

  async function load(): Promise<void> {
    const expected = ++generation;
    section.hidden = false;
    section.replaceChildren(...head(), node("p", "so-state", "Loading"));
    try {
      const [help, billing] = await Promise.all([host.readHelp(), host.readBilling()]);
      if (expected !== generation) return;
      if (help.status !== 200 || help.body === null) {
        const retry = node("button", "btn-secondary", "Try again");
        retry.type = "button";
        retry.onclick = () => void load();
        section.replaceChildren(...head(), node("p", "so-state", "AI help is unavailable right now."), retry);
        return;
      }
      const list = node("dl", "cr-ai-help-list");
      drawStatus(row(list, "Use AI help", "ai-help-status", "Turn this off to stop your team using AI help at this location."), help.body, expected);
      drawBilled(row(list, "How it is billed", "ai-help-billed"), help.body);
      const usage = billing.status === 200 ? billing.body : null;
      if (usage !== null) {
        const monthLabel = usage.closed ? monthName(usage.month) : "This month";
        drawMonth(row(list, monthLabel, "ai-help-month"), usage);
        if (!usage.closed) drawLimits(row(list, "Monthly limit", "ai-help-limit", "When this location's AI charges reach the limit, AI requests stop until next month. Leave it blank for no limit."), usage, expected);
      }
      section.replaceChildren(...head(), list, drawRecent(help.body));
    } catch {
      if (expected === generation) {
        const retry = node("button", "btn-secondary", "Try again");
        retry.type = "button";
        retry.onclick = () => void load();
        section.replaceChildren(...head(), node("p", "so-state", "AI help is unavailable right now."), retry);
      }
    }
  }

  return { load, reset };
}
